<template>
  <div class="error-boundary" role="alert" :aria-label="errorLabel">
    <div class="error-content">
      <div class="error-icon">⚠️</div>
      <div class="error-details">
        <h3 class="error-title">{{ title }}</h3>
        <p class="error-message">{{ message }}</p>
        <div v-if="details" class="error-details-text">
          <details>
            <summary class="error-summary">Show technical details</summary>
            <pre class="error-pre">{{ details }}</pre>
          </details>
        </div>
      </div>
    </div>
    
    <div class="error-actions">
      <button 
        v-if="showRetry"
        @click="handleRetry"
        class="retry-button"
        :disabled="retrying"
        :aria-label="retrying ? 'Retrying...' : 'Retry loading'"
      >
        <span v-if="retrying" class="retry-spinner"></span>
        <span>{{ retrying ? 'Retrying...' : 'Try Again' }}</span>
      </button>
      
      <button 
        v-if="showReload"
        @click="handleReload"
        class="reload-button"
        aria-label="Reload page"
      >
        Reload Page
      </button>
    </div>
    
    <div v-if="retryCount > 0" class="retry-info">
      Retry attempt {{ retryCount }}/{{ maxRetries }}
    </div>
  </div>
</template>

<script>
export default {
  name: 'ErrorBoundary',
  props: {
    title: {
      type: String,
      default: 'Something went wrong'
    },
    message: {
      type: String,
      default: 'We encountered an error while loading the data. Please try again.'
    },
    details: {
      type: String,
      default: ''
    },
    showRetry: {
      type: Boolean,
      default: true
    },
    showReload: {
      type: Boolean,
      default: false
    },
    maxRetries: {
      type: Number,
      default: 3
    },
    errorLabel: {
      type: String,
      default: 'Error occurred'
    }
  },
  emits: ['retry', 'reload'],
  data() {
    return {
      retrying: false,
      retryCount: 0
    }
  },
  methods: {
    async handleRetry() {
      if (this.retryCount >= this.maxRetries) {
        return
      }
      
      this.retrying = true
      this.retryCount++
      
      try {
        await new Promise(resolve => setTimeout(resolve, 500)) // Brief delay for UX
        this.$emit('retry', this.retryCount)
      } catch (error) {
        console.error('Retry failed:', error)
      } finally {
        // Reset retrying state after a delay
        setTimeout(() => {
          this.retrying = false
        }, 1000)
      }
    },
    
    handleReload() {
      this.$emit('reload')
      window.location.reload()
    }
  },
  
  // Reset retry count when component is recreated
  created() {
    this.retryCount = 0
  }
}
</script>

<style scoped>
.error-boundary {
  background: rgba(244, 67, 54, 0.1);
  border: 1px solid rgba(244, 67, 54, 0.3);
  border-radius: 12px;
  padding: 24px;
  margin: 24px 0;
  color: #ff8a80;
}

.error-content {
  display: flex;
  align-items: flex-start;
  gap: 16px;
  margin-bottom: 20px;
}

.error-icon {
  font-size: 32px;
  flex-shrink: 0;
  margin-top: 4px;
}

.error-details {
  flex: 1;
}

.error-title {
  font-size: 18px;
  font-weight: 600;
  margin: 0 0 8px 0;
  color: #ffcdd2;
}

.error-message {
  font-size: 14px;
  margin: 0 0 12px 0;
  line-height: 1.5;
  color: #ff8a80;
}

.error-details-text {
  margin-top: 12px;
}

.error-summary {
  font-size: 12px;
  color: #ffab91;
  cursor: pointer;
  padding: 4px 0;
  border-bottom: 1px dotted rgba(255, 171, 145, 0.5);
  display: inline-block;
  margin-bottom: 8px;
}

.error-summary:hover {
  color: #ffcdd2;
}

.error-pre {
  font-size: 11px;
  background: rgba(0, 0, 0, 0.2);
  padding: 12px;
  border-radius: 6px;
  margin: 8px 0 0 0;
  overflow-x: auto;
  color: #ffcdd2;
  white-space: pre-wrap;
  word-break: break-word;
}

.error-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.retry-button,
.reload-button {
  padding: 10px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;
  border: none;
  display: flex;
  align-items: center;
  gap: 8px;
}

.retry-button {
  background: rgba(244, 67, 54, 0.8);
  color: #ffffff;
}

.retry-button:hover:not(:disabled) {
  background: rgba(244, 67, 54, 0.9);
  transform: translateY(-1px);
}

.retry-button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
  transform: none;
}

.reload-button {
  background: rgba(255, 255, 255, 0.1);
  color: #ff8a80;
  border: 1px solid rgba(244, 67, 54, 0.3);
}

.reload-button:hover {
  background: rgba(255, 255, 255, 0.15);
  border-color: rgba(244, 67, 54, 0.5);
  transform: translateY(-1px);
}

.retry-spinner {
  width: 12px;
  height: 12px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-left-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.retry-info {
  margin-top: 12px;
  font-size: 12px;
  color: #ffab91;
  text-align: center;
  font-style: italic;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* Mobile responsive */
@media (max-width: 768px) {
  .error-boundary {
    padding: 16px;
    margin: 16px 0;
  }
  
  .error-content {
    flex-direction: column;
    gap: 12px;
  }
  
  .error-icon {
    align-self: center;
    margin-top: 0;
  }
  
  .error-actions {
    justify-content: center;
  }
  
  .retry-button,
  .reload-button {
    flex: 1;
    justify-content: center;
    min-width: 120px;
  }
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .retry-button,
  .reload-button {
    transition: none;
  }
  
  .retry-button:hover:not(:disabled),
  .reload-button:hover {
    transform: none;
  }
  
  .retry-spinner {
    animation: none;
    border-left-color: rgba(255, 255, 255, 0.6);
  }
}

/* High contrast mode support */
@media (prefers-contrast: high) {
  .error-boundary {
    border: 2px solid #ff0000;
    background: rgba(255, 0, 0, 0.2);
  }
  
  .retry-button {
    border: 1px solid #ffffff;
  }
  
  .reload-button {
    border: 2px solid #ffffff;
  }
}
</style>